import AddCircleIcon from '@mui/icons-material/AddCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ClearIcon from '@mui/icons-material/Clear';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Palette, PaletteColor, Skeleton, alpha } from '@mui/material';
import MuiChip, { ChipProps } from '@mui/material/Chip';
import { ElementType, MouseEvent, ReactElement, forwardRef, useId, useState } from 'react';
import { useTheme } from 'styled-components';
import { Icon, type IconName } from '../icon/index';
import { MuiMenu, MuiMenuItem } from '../mui-list/index';

export type ChipSize = 'small' | 'medium' | 'large';

export interface ExtendedChipProps extends Omit<ChipProps, 'size'> {
	/**
	 * Provides the `Chip` with a maximum length, and cuts the label off with an ellipsis.
	 */
	maxWidth?: string | number;
	center?: boolean;
	disableEllipses?: boolean;
	size?: ChipSize;
}

type ValidKeys<T, TValue> = {
	[K in keyof T]: T[K] extends TValue ? K : never;
}[keyof T];

export type PaletteColors = ValidKeys<Palette, PaletteColor>;
export type DataChipColor = 'default' | 'magenta' | 'purple' | 'teal' | 'green' | 'orange' | 'blue' | 'light';
export type StatusChipColor = 'default' | 'error' | 'warning' | 'info' | 'success';
export type StatusChipType = 'standard' | 'sentiment';

type DataChipColourProps =
	| {
			color?: DataChipColor;
			legacyColor?: never;
	  }
	| {
			color?: never;
			/**
			 * Escape hatch for colours loaded from persisted data.
			 *
			 * @deprecated Prefer the decorative `color` options. Do not use this for new product-authored chips.
			 */
			legacyColor: string;
	  };

export type DataChipProps = Omit<ExtendedChipProps, 'color' | 'variant'> &
	DataChipColourProps & {
		component?: ElementType;
		href?: string;
		target?: string;
		rel?: string;
	};

export interface StatusChipOption {
	value: string;
	label: string;
	color: StatusChipColor;
	disabled?: boolean;
}

export interface StatusChipSelect {
	value: string;
	options: readonly StatusChipOption[];
	onChange: (value: string, option: StatusChipOption) => void;
	ariaLabel?: string;
}

export interface IStatusChip extends Omit<ExtendedChipProps, 'avatar' | 'color' | 'deleteIcon' | 'label' | 'onDelete' | 'title' | 'type'> {
	label?: string;
	color?: StatusChipColor;
	type?: StatusChipType;
	select?: StatusChipSelect;
	title?: string | false;
	/**
	 * Escape hatch for status colours loaded from persisted configuration.
	 *
	 * @deprecated Prefer the semantic `color` options. Do not use this for new product-authored chips.
	 */
	legacyColor?: string;
	/** @deprecated Use the semantic `color` prop. Non-semantic values will be removed after usages are migrated to DataChip. */
	backgroundColor?: PaletteColors;
	/** @deprecated StatusChip owns its leading state indicator. */
	icon?: ReactElement;
	/** @deprecated StatusChip owns the colour of its status dot. */
	iconColor?: PaletteColors;
	/** @deprecated Status chips do not have a disabled state in FoxUI. */
	disabled?: boolean;
}

export interface IStatusChipNew extends Omit<ExtendedChipProps, 'title' | 'color'> {
	color?: PaletteColors;
	label: string;
}

const CHIP_HEIGHTS: Record<ChipSize, 'height-chip-sm' | 'height-chip-md' | 'height-chip-lg'> = {
	small: 'height-chip-sm',
	medium: 'height-chip-md',
	large: 'height-chip-lg'
};

const CHIP_OUTER_PADDING: Record<ChipSize, number> = {
	small: 5,
	medium: 6,
	large: 8
};

const CHIP_ADORNMENT_SIZE: Record<ChipSize, number> = {
	small: 16,
	medium: 24,
	large: 24
};

const CHIP_ADORNMENT_CONTENT_SIZE: Record<ChipSize, number> = {
	small: 14,
	medium: 20,
	large: 20
};

const isPaletteColor = (value: unknown): value is PaletteColor =>
	typeof value === 'object' && value !== null && 'main' in value && typeof value.main === 'string';

/**
 * Low-level chip primitive shared by DataChip and StatusChip.
 *
 * Product code should normally use one of the opinionated components so decorative and semantic
 * colours cannot be confused.
 */
export const Chip = forwardRef<HTMLDivElement, ExtendedChipProps>(
	({ maxWidth, disabled, center, disableEllipses, size = 'medium', style, sx, deleteIcon, ...props }, ref) => {
		const theme = useTheme();
		const muiSize = size === 'large' ? 'medium' : size;
		const borderWidth = props.variant === 'outlined' || (props.color && props.color !== 'default') ? 1 : 0;
		const outerPadding = CHIP_OUTER_PADDING[size] - borderWidth;
		const adornmentSize = CHIP_ADORNMENT_SIZE[size];
		const adornmentContentSize = CHIP_ADORNMENT_CONTENT_SIZE[size];
		const adornmentPadding = (adornmentSize - adornmentContentSize) / 2;

		return (
			<MuiChip
				{...props}
				style={{
					overflow: disableEllipses ? 'visible' : 'hidden',
					textOverflow: disableEllipses ? undefined : 'ellipsis',
					whiteSpace: disableEllipses ? undefined : 'nowrap',
					maxWidth,
					margin: center ? 'auto' : undefined,
					border: props.color && props.color !== 'default' ? `1px solid ${alpha(theme.palette[props.color].light, 0.2)}` : undefined,
					...style,
					flexBasis: 'auto',
					flexGrow: 0,
					flexShrink: 0,
					justifyContent: 'flex-start',
					width: 'fit-content'
				}}
				ref={ref}
				deleteIcon={deleteIcon ?? <ClearIcon style={{ fontSize: size === 'small' ? '1rem' : '1.25rem' }} />}
				disabled={disabled}
				size={muiSize}
				sx={[
					{
						height: chipTheme => chipTheme.shape[CHIP_HEIGHTS[size]],
						paddingLeft: `${outerPadding}px`,
						paddingRight: `${outerPadding}px`,
						borderRadius: chipTheme => `${chipTheme.shape[size === 'small' ? 'radius-sm' : 'radius-md']}px`,
						fontSize: size === 'small' ? '0.875rem' : '0.9375rem',
						fontWeight: 500,
						lineHeight: 1.2,
						letterSpacing: size === 'small' ? '0.4px' : '0.46px',
						'&& .MuiChip-label': {
							paddingLeft: '6px',
							paddingRight: '6px',
							overflow: disableEllipses ? 'visible' : 'hidden',
							textAlign: 'left',
							textOverflow: disableEllipses ? undefined : 'ellipsis',
							whiteSpace: disableEllipses ? undefined : 'nowrap'
						},
						'&& .MuiChip-icon, && .MuiChip-deleteIcon': {
							boxSizing: 'content-box',
							flexShrink: 0,
							height: adornmentContentSize,
							marginLeft: 0,
							marginRight: 0,
							padding: `${adornmentPadding}px`,
							width: adornmentContentSize
						},
						'&& .MuiChip-avatar': {
							height: adornmentSize,
							marginLeft: 0,
							marginRight: 0,
							width: adornmentSize
						}
					},
					...(Array.isArray(sx) ? sx : sx ? [sx] : [])
				]}
			/>
		);
	}
);

/**
 * Labels a piece of data such as a tag, category, filter, or selected item.
 *
 * Colour is decorative. Use StatusChip when colour communicates record or process state.
 */
export const DataChip = forwardRef<HTMLDivElement, DataChipProps>(
	({ color = 'default', legacyColor, clickable, onClick, onDelete, sx, ...props }, ref) => {
		const isInteractive = Boolean(clickable || onClick || onDelete);

		return (
			<Chip
				{...props}
				ref={ref}
				clickable={clickable}
				onClick={onClick}
				onDelete={onDelete}
				variant="outlined"
				data-chip-type="data"
				data-legacy-color={legacyColor ? 'true' : undefined}
				sx={[
					theme => {
						const chipPalette = theme.palette._components.chip;
						const paletteColor = legacyColor ? theme.palette[legacyColor as PaletteColors] : undefined;
						const resolvedLegacyColor = isPaletteColor(paletteColor) ? paletteColor.main : legacyColor;
						const token = resolvedLegacyColor
							? {
									text: theme.palette.getContrastText(resolvedLegacyColor),
									background: resolvedLegacyColor,
									outline: resolvedLegacyColor,
									hover: resolvedLegacyColor,
									pressed: resolvedLegacyColor,
									focus: resolvedLegacyColor
								}
							: chipPalette.data[color];

						return {
							color: token.text,
							backgroundColor: token.background,
							border: `1px solid ${token.outline}`,
							'& .MuiChip-icon, & .MuiChip-deleteIcon': {
								color: 'inherit'
							},
							...(isInteractive && {
								'&:hover': {
									backgroundColor: token.hover
								},
								'&:active': {
									backgroundColor: token.pressed
								}
							}),
							'&.Mui-focusVisible': {
								backgroundColor: token.focus,
								border: `2px solid ${chipPalette.focusOutline}`
							},
							'&.Mui-disabled': {
								opacity: 0.38
							}
						};
					},
					...(Array.isArray(sx) ? sx : sx ? [sx] : [])
				]}
			/>
		);
	}
);

const STATUS_CHIP_COLORS: readonly StatusChipColor[] = ['default', 'error', 'warning', 'info', 'success'];
const STATUS_CHIP_ICON_GAP: Record<ChipSize, number> = {
	small: -4,
	medium: -2,
	large: -2
};

const STATUS_CHIP_ICON_SIZE: Record<ChipSize, number> = {
	small: 20,
	medium: 24,
	large: 24
};

const STATUS_CHIP_SENTIMENT_ICON_SIZE: Record<ChipSize, number> = {
	small: 16,
	medium: 20,
	large: 20
};

const STATUS_CHIP_SENTIMENT_ICONS: Partial<Record<StatusChipColor, IconName>> = {
	default: 'SentimentNeutral',
	success: 'Mood',
	warning: 'MoodBad'
};

const STATUS_CHIP_OUTER_PADDING: Record<ChipSize, number> = {
	small: 4,
	medium: 5,
	large: 7
};

const isStatusChipColor = (color: unknown): color is StatusChipColor =>
	typeof color === 'string' && STATUS_CHIP_COLORS.includes(color as StatusChipColor);

/**
 * Communicates the state of a record or process.
 *
 * StatusChip owns its leading state indicator and only accepts semantic colours. It defaults to a
 * status dot; use the sentiment type for the fixed semantic face icon. Pass `select` when the status
 * is editable; the component owns the menu, selected label, colour, and dropdown adornment.
 */
export const StatusChip = forwardRef<HTMLDivElement, IStatusChip>(function StatusChip(
	{
		maxWidth,
		backgroundColor,
		iconColor,
		label,
		icon,
		center,
		title,
		color,
		type = 'standard',
		select,
		legacyColor,
		disabled,
		onClick,
		size = 'medium',
		sx,
		...props
	},
	ref
) {
	const theme = useTheme();
	const chipPalette = theme.palette._components.chip;
	const menuId = useId();
	const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
	const selectedOption = select?.options.find(option => option.value === select.value);
	const resolvedLabel = selectedOption?.label ?? label ?? select?.value ?? '';
	const resolvedColor = selectedOption?.color ?? color ?? (isStatusChipColor(backgroundColor) ? backgroundColor : 'default');
	const legacyColorSource = legacyColor ?? (backgroundColor && !isStatusChipColor(backgroundColor) ? backgroundColor : undefined);
	const legacyPaletteCandidate = legacyColorSource ? theme.palette[legacyColorSource as PaletteColors] : undefined;
	const legacyPalette = isPaletteColor(legacyPaletteCandidate) ? legacyPaletteCandidate : undefined;
	const resolvedLegacyColor = legacyPalette ? undefined : legacyColorSource;
	const legacyText = legacyPalette?.dark ?? (resolvedLegacyColor ? theme.palette.getContrastText(resolvedLegacyColor) : undefined);
	const token = chipPalette.status[resolvedColor];
	const hideTitle = title === false;
	const open = Boolean(anchorElement);
	const isInteractive = Boolean(select || onClick);
	const isSentiment = type === 'sentiment';
	const iconName: IconName = isSentiment ? (STATUS_CHIP_SENTIMENT_ICONS[resolvedColor] ?? 'SentimentNeutral') : 'StatusDot';
	const iconSize = isSentiment ? STATUS_CHIP_SENTIMENT_ICON_SIZE[size] : STATUS_CHIP_ICON_SIZE[size];
	const iconSlotWidth = isSentiment ? (size === 'medium' ? (resolvedColor === 'default' ? 19 : 20) : 18) : 18;
	const iconGap = isSentiment ? 0 : STATUS_CHIP_ICON_GAP[size];

	const handleClick = (event: MouseEvent<HTMLDivElement>) => {
		onClick?.(event);
		if (select && !event.defaultPrevented) setAnchorElement(event.currentTarget);
	};

	const handleSelect = (option: StatusChipOption) => {
		select?.onChange(option.value, option);
		setAnchorElement(null);
	};

	const chipIcon = <span className="SwiftFoxStatusChip-icon">{icon ?? <Icon iconName={iconName} />}</span>;

	return (
		<>
			<Chip
				{...props}
				ref={ref}
				aria-controls={open ? menuId : undefined}
				aria-expanded={select ? open : undefined}
				aria-haspopup={select ? 'menu' : undefined}
				aria-label={select?.ariaLabel}
				clickable={isInteractive}
				center={center}
				data-chip-type="status"
				data-legacy-color={legacyColorSource ? 'true' : undefined}
				data-status-chip-type={type}
				disabled={disabled}
				icon={chipIcon}
				label={
					select ? (
						<Box component="span" className="SwiftFoxStatusChip-label" sx={{ alignItems: 'center', display: 'inline-flex', gap: 1 }}>
							{resolvedLabel}
							<KeyboardArrowDownIcon aria-hidden="true" sx={{ fontSize: '1.25rem' }} />
						</Box>
					) : (
						resolvedLabel
					)
				}
				maxWidth={maxWidth}
				onClick={isInteractive ? handleClick : undefined}
				size={size}
				title={hideTitle ? undefined : (title ?? resolvedLabel)}
				variant="outlined"
				sx={[
					{
						color: legacyText ?? token.text,
						backgroundColor: legacyPalette ? alpha(legacyPalette.light, 0.2) : (resolvedLegacyColor ?? token.background),
						border: `1px solid ${legacyPalette ? alpha(legacyPalette.light, 0.2) : (resolvedLegacyColor ?? token.outline)}`,
						borderRadius: '100px',
						paddingLeft: `${STATUS_CHIP_OUTER_PADDING[size]}px`,
						paddingRight: `${STATUS_CHIP_OUTER_PADDING[size]}px`,
						'&& .MuiChip-label': {
							paddingLeft: '6px',
							paddingRight: '6px'
						},
						'&& .MuiChip-icon': {
							alignItems: 'center',
							boxSizing: 'border-box',
							color: iconColor ? theme.palette[iconColor].dark : (legacyText ?? token.text),
							display: 'inline-flex',
							flex: `0 0 ${iconSlotWidth}px`,
							height: `${iconSize}px`,
							justifyContent: 'center',
							marginLeft: 0,
							marginRight: `${iconGap}px`,
							overflow: 'visible',
							padding: 0,
							width: `${iconSlotWidth}px`,
							'& > svg': {
								flex: '0 0 auto',
								height: `${iconSize}px`,
								width: `${iconSize}px`
							}
						},
						...(isInteractive && {
							'&:hover': {
								backgroundColor: legacyPalette ? alpha(legacyPalette.light, 0.5) : (resolvedLegacyColor ?? token.hover)
							},
							'&:active': {
								backgroundColor: legacyPalette?.light ?? resolvedLegacyColor ?? token.pressed
							}
						}),
						'&.Mui-focusVisible': {
							backgroundColor: legacyPalette ? alpha(legacyPalette.main, 0.3) : (resolvedLegacyColor ?? token.focus),
							border: `2px solid ${chipPalette.focusOutline}`
						}
					},
					...(Array.isArray(sx) ? sx : sx ? [sx] : [])
				]}
			/>
			{select ? (
				<MuiMenu
					id={menuId}
					anchorEl={anchorElement}
					open={open}
					onClose={() => setAnchorElement(null)}
					MenuListProps={{ 'aria-label': select.ariaLabel ?? `Change ${resolvedLabel} status` }}
				>
					{select.options.map(option => {
						const optionToken = chipPalette.status[option.color];

						return (
							<MuiMenuItem
								key={option.value}
								disabled={option.disabled}
								selected={option.value === select.value}
								onClick={() => handleSelect(option)}
								sx={{ gap: 2 }}
							>
								<Icon
									iconName={isSentiment ? (STATUS_CHIP_SENTIMENT_ICONS[option.color] ?? 'SentimentNeutral') : 'StatusDot'}
									customColor={optionToken.text}
								/>
								{option.label}
							</MuiMenuItem>
						);
					})}
				</MuiMenu>
			) : null}
		</>
	);
});

/**
 * @deprecated Use StatusChip. This adapter remains only while existing usages are migrated.
 */
export const StatusChipNew = ({ color, label, ...props }: IStatusChipNew) => (
	<StatusChip
		{...props}
		label={label}
		color={isStatusChipColor(color) ? color : undefined}
		backgroundColor={!isStatusChipColor(color) ? color : undefined}
	/>
);

/** @deprecated Import the icon directly instead. */
export const AddPersonIcon = () => {
	return <PersonAddIcon />;
};
/** @deprecated Import the icon directly instead. */
export const NewPersonIcon = () => {
	return <AddCircleIcon />;
};
/** @deprecated Import the icon directly instead. */
export const WarningPersonIcon = () => {
	return <WarningIcon />;
};
/** @deprecated Import the icon directly instead. */
export const SuccessDataIcon = () => {
	return <CheckCircleIcon />;
};
/** @deprecated Import the icon directly instead. */
export const WarningDataIcon = () => {
	return <CancelIcon />;
};

/** @deprecated Import the icon directly instead. */
export const SkipIcon = () => {
	return <DoNotDisturbIcon />;
};

const SENTIMENT_COLORS: Record<'Positive' | 'Neutral' | 'Negative', StatusChipColor> = {
	Positive: 'success',
	Neutral: 'default',
	Negative: 'warning'
};

export const SentimentChip = ({
	sentiment,
	title,
	loading,
	alignSelf,
	size = 'small'
}: {
	size?: ChipSize;
	sentiment?: keyof typeof SENTIMENT_COLORS | null;
	title?: string;
	loading?: boolean;
	alignSelf?: 'start' | 'end';
}) => {
	if (loading) {
		return <Skeleton variant="text" width="10ch" />;
	}

	if (!sentiment) return null;

	return (
		<StatusChip type="sentiment" size={size} color={SENTIMENT_COLORS[sentiment]} style={{ alignSelf }} label={sentiment} title={title ?? false} />
	);
};
